import { z } from 'zod';
import { LogoDataSchema } from '../../shared/logo.schema';
/**
 * Tenant Type Enum
 */ export const TenantTypeSchema = z.enum([
    'partner',
    'customer',
    'demo'
]);
/**
 * Tenant types that never run the Ersteinrichtung wizard. The wizard lives
 * under the customer app's `/$slug` routes, so a tenant type routed elsewhere
 * can never reach it — gating one would lock its entire write surface with
 * nothing in the system able to unlock it.
 *
 * Stated as the EXEMPT list rather than the requiring one so the default is to
 * gate: this predicate short-circuits SetupGateGuard, and a tenant type added
 * without answering the question here stays gated — a visible bug, rather than
 * a silent bypass of a mandatory step.
 */ const TENANT_TYPES_EXEMPT_FROM_INITIAL_SETUP = [
    'partner'
];
export const requiresInitialSetup = (tenantType)=>!TENANT_TYPES_EXEMPT_FROM_INITIAL_SETUP.includes(tenantType);
/**
 * Tenant types that run somewhere other than the customer app (processes,
 * systems, AI, app entitlements). Named the same way as the setup exemption
 * above and for the same reason: a tenant type added without answering the
 * question here is treated as a customer app, which is the visible, harmless
 * default — it renders customer surfaces rather than hiding real ones.
 *
 * Separate from `requiresInitialSetup` on purpose. The two lists happen to hold
 * the same value today, but they answer different questions ("must it run the
 * Ersteinrichtung wizard?" vs "does it use the customer app at all?").
 */ const TENANT_TYPES_OUTSIDE_CUSTOMER_APP = [
    'partner'
];
export const runsCustomerApp = (tenantType)=>!TENANT_TYPES_OUTSIDE_CUSTOMER_APP.includes(tenantType);
/**
 * Tenant types whose seats are not counted against the per-role limits. Partner
 * tenants staff themselves through the partner app and were never sold by seat,
 * so counting them would block their own team growth.
 *
 * Stated as the EXEMPT list for the same reason as its siblings above: this
 * predicate short-circuits the seat check, so a tenant type added without
 * answering the question here stays enforced — the safe side, where a wrongly
 * limited tenant is a visible refusal rather than a silently uncapped one.
 */ const TENANT_TYPES_EXEMPT_FROM_SEAT_LIMITS = [
    'partner'
];
export const enforcesSeatLimits = (tenantType)=>!TENANT_TYPES_EXEMPT_FROM_SEAT_LIMITS.includes(tenantType);
/**
 * Tenant types that are not real customer usage — the superadmin recording
 * analytics (fan-out and scope picker) exclude them so they cannot skew the
 * averages.
 *
 * Unlike the predicate siblings above, the list itself is exported too: the
 * backend consumes it verbatim inside a Mongo filter.
 */ export const TENANT_TYPES_EXCLUDED_FROM_ANALYTICS = [
    'partner',
    'demo'
];
export const countsTowardAnalytics = (tenantType)=>!TENANT_TYPES_EXCLUDED_FROM_ANALYTICS.includes(tenantType);
/** Free-text superadmin note ("Anmerkung"), e.g. "Demo for XYZ". */ export const TenantDescriptionSchema = z.string().trim().max(2000);
/**
 * Canonical tenant slug. URLs and deep-links are case-folded, so the value is
 * normalized to a single lowercase, trimmed, hyphen-only form — that is what
 * makes uniqueness meaningful ("Acme" and "acme" must not both exist). The
 * shared `slugify` helper already emits a slug in this accepted shape.
 */ export const TenantSlugSchema = z.string().trim().toLowerCase().min(1, 'Slug is required').regex(/^[a-z0-9-]+$/, 'Slug may contain only lowercase letters, numbers, and hyphens');
/**
 * The identity fields a superadmin supplies when creating a tenant — shared
 * verbatim by add-tenant and (extended with demo fields) by clone-tenant.
 */ export const TenantIdentityRequestSchema = z.object({
    name: z.string(),
    slug: TenantSlugSchema,
    tenantType: TenantTypeSchema,
    expiresAt: z.iso.datetime().nullable().optional(),
    description: TenantDescriptionSchema.optional()
});
/** Superadmin-set cap on one seat pool. `0` is a real value: no seats at all. */ export const SeatLimitSchema = z.number().int().min(0);
/**
 * Per-role seat caps a superadmin sets on a tenant. Absent or `null` means
 * unlimited and `0` means zero seats — the distinction is load-bearing on every
 * read and write, so a missing pool must never be coerced to `0`.
 */ export const TenantSeatLimitsSchema = z.object({
    maxSystemAdmins: SeatLimitSchema.nullable().optional(),
    maxOrgManagers: SeatLimitSchema.nullable().optional(),
    maxMembers: SeatLimitSchema.nullable().optional()
});
/**
 * What a tenant may set up for others (plan 050): the kinds of workspace it
 * may create and then manage. Set only by a superadmin; absent means none.
 * An interim gate until the tenant's licence carries the right (plan 051),
 * which is why the backend reads it in one place only.
 */ export const TenantProvisioningSchema = z.object({
    tenantTypes: z.array(TenantTypeSchema).min(1)
});
/**
 * Tenant Schema
 * Represents a tenant entity in the system
 */ export const TenantSchema = z.object({
    _id: z.string(),
    name: z.string(),
    slug: z.string(),
    active: z.boolean(),
    tenantType: TenantTypeSchema,
    expiresAt: z.iso.datetime().nullable(),
    /**
   * The account this tenant belongs to — the customer, who holds every external
   * id (HubSpot company, Stripe customer, Moco company). One account may own
   * several tenants: a consultancy runs a partner workspace and a customer
   * workspace off one contract.
   */ accountId: z.string().optional(),
    description: TenantDescriptionSchema.optional(),
    seatLimits: TenantSeatLimitsSchema.optional(),
    provisioning: TenantProvisioningSchema.optional(),
    /**
   * The tenant that created this one through provisioning and may manage it.
   * Absent for tenants a superadmin made.
   */ managedByTenantId: z.string().optional(),
    logo: LogoDataSchema.optional()
});
/**
 * The tenant shape returned by the add/update/clone mutations (no logo — the
 * logo has its own upload endpoint and rides only on the list/detail reads).
 */ export const TenantSummarySchema = TenantSchema.omit({
    logo: true
}).extend({
    /**
   * DERIVED from the tenant's account, never stored on the tenant: the HubSpot
   * company this customer is. Read-only on the wire — writes set it on the
   * account (the tenant mutations accept it and resolve the account behind it).
   * Absent when the tenant has no account, or the account no company.
   */ hubspotCompanyId: z.string().optional(),
    /**
   * DERIVED as well: the account's company name, so a tenant list and a
   * tenant page can name and link the customer without a second read. Absent
   * when the tenant has no account, or the account no name yet.
   */ accountName: z.string().optional()
});

//# sourceMappingURL=tenant.schema.js.map